import { getLocalStorage, setLocalStorage, removeLocalStorage } from '@/lib/isomorphic'
import type { SettingKey, SettingValueTypes } from '@/types/settings'

const STORAGE_PREFIX = 'ps_setting_'

/**
 * Get the localStorage key for a setting
 */
export function getSettingStorageKey(key: SettingKey): string {
  return `${STORAGE_PREFIX}${key}`
}

/**
 * Get a setting value from localStorage
 */
export function getLocalSetting<K extends SettingKey>(
  key: K
): SettingValueTypes[K] | null {
  const storageKey = getSettingStorageKey(key)
  const stored = getLocalStorage(storageKey)
  
  if (stored === null) return null
  
  try {
    return JSON.parse(stored) as SettingValueTypes[K]
  } catch {
    // If parsing fails, return the raw string for backward compatibility
    return stored as SettingValueTypes[K]
  }
}

/**
 * Set a setting value in localStorage
 */
export function setLocalSetting<K extends SettingKey>(
  key: K,
  value: SettingValueTypes[K]
): void {
  const storageKey = getSettingStorageKey(key)
  setLocalStorage(storageKey, JSON.stringify(value))
}

/**
 * Remove a setting value from localStorage
 */
export function removeLocalSetting(key: SettingKey): void {
  const storageKey = getSettingStorageKey(key)
  removeLocalStorage(storageKey)
}

/**
 * Check if a setting has a localStorage override
 */
export function hasLocalSetting(key: SettingKey): boolean {
  return getLocalSetting(key) !== null
}

/**
 * Clear all local setting overrides
 */
export function clearAllLocalSettings(): void {
  // This would require iterating localStorage keys
  // For now, we'll implement this if needed
  console.warn('clearAllLocalSettings not yet implemented')
}

/**
 * Default values for all settings
 */
export const SETTING_DEFAULTS: SettingValueTypes = {
  'call:caller_id_mode': 'anonymous',
  'ui:theme': 'system',
  'audio:input_device': null,
  'call:agent_voice': null,
}

/**
 * Get the default value for a setting
 */
export function getSettingDefault<K extends SettingKey>(key: K): SettingValueTypes[K] {
  return SETTING_DEFAULTS[key]
}
